import React, { Fragment, useContext, useEffect, useState } from "react";
import { Box, TextField, Button, Grid, Typography, Link } from "@mui/material";
import { Formik } from "formik";
import { useNavigate } from "react-router-dom";
import AuthLayout from "../common/AuthLayout/AuthLayout";
import MainNavigation from "../MainNavigation/MainNavigation";
import { useMutation } from "@apollo/client/react";
import get from "lodash/get";
import authService from "../../services/authService";
import AppContext from "../../context/AppContext";
import styles from "./UnsubscribeDiabetes.module.css";
import * as yup from "yup";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { validateMedicare } from "@ehealth/ehealthutils";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { UnsubscribeDiabeticPatientMutation, UnsubscribeDiabeticPatientMutationVariables } from "./types/UnsubscribeDiabeticPatientMutation";
import UnsubscribeDiabeticPatient from "./UnsubscribeDiabeticPatientMutation";
import Loading from "../Loading/Loading";
import { CombinedGraphQLErrors, ServerError } from "@apollo/client/errors";
import { Dayjs } from "dayjs";

type UnsubscribeDiabetesBoxType = {
  onSubmit: (firstname: string, lastname: string, medicare: string, dob: string) => Promise<void>;
  error?: any;
  loading?: boolean;
  submitSuccess: boolean;
  message: string;
};

export const UnsubscribeDiabetesBox: React.FC<UnsubscribeDiabetesBoxType> = (props) => {
  const { error, loading, submitSuccess, message } = props;
  const hist = useNavigate();
  const initialValues = {
    firstname: "",
    lastname: "",
    medicare: "",
    dob: "",
  };

  const isDesktop = useContext(AppContext).isDesktop;

  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [medicare, setMedicare] = useState("");
  const [dob, setDob] = useState<Date | null | undefined>(null);

  const formValidationSchema = yup.object().shape({
    firstname: yup.string().required("Firstname is required"),
    lastname: yup.string().required("Lastname is required"),
    dob: yup.date().required("Date of Birth is required").typeError("Date is invalid"),
    medicare: yup
      .string()
      .required("Medicare is required")
      .min(12, "11 Digit Number required")
      .test("Medicare Test", "Invalid Medicare Number", (value) =>
        value && value.length ? validateMedicare(value as string) : true,
      ),
  });

  const submit = () => {
    const newMedicare = medicare.replace("/", "");
    const newDob = Intl.DateTimeFormat("sv-SE").format(dob as Date);

    props.onSubmit(firstname, lastname, newMedicare, newDob);
  };

  const handleCancel = () => {
    hist("/login");
  };

  useEffect(() => {
    if (props.submitSuccess) {
      setFirstname("");
      setLastname("");
      setMedicare("");
      setDob(null);
    }
  }, [props.submitSuccess]);

  return (
    <Grid container className={isDesktop ? styles.mainBox : styles.mainBoxM}>
      <Grid size={12}>
        <AuthLayout title="Unsubscribe from Diabetes Reminder" maxWidth={"690px"}>
          <Box mt={4}>
            <Box textAlign={"right"} mb={1} mt={-4}></Box>
            <Formik
              validationSchema={formValidationSchema}
              enableReinitialize={false}
              initialValues={initialValues}
              onSubmit={submit}
            >
              {({ setFieldValue, errors, handleBlur, handleSubmit, touched }) => {
                return (
                  <form onSubmit={handleSubmit}>
                    {!authService.getSharedAccount() && (
                      <Box>
                        <Typography color="primary" className={styles.subHeading}>
                          Our Diabetes Clinical Evaluation Program provides an innovative service for doctors to ensure
                          their patients are kept up to date with blood tests recommended for appropriate diabetes
                          monitoring. If your doctor is enrolled in this program, you may receive reminder and recall
                          letters when your review appointment or blood test is due. If you wish to opt out of the
                          reminder service please fill in the details below to submit your request
                        </Typography>
                      </Box>
                    )}
                    <Box>
                      {error && (() => {
                        if (CombinedGraphQLErrors.is(error)) {
                          return error.errors.map((graphqlError, index) => (
                            <Typography key={index} color="error">
                              {graphqlError.message}
                            </Typography>
                          ));
                        } else if (ServerError.is(error)) {
                          return <Typography color="error">{error.message}</Typography>;
                        } else {
                          return <Typography color="error">{(error as any)?.message || 'An error occurred'}</Typography>;
                        }
                      })()}
                    </Box>
                    <Box mt={2}>
                      <TextField
                        id="firstname"
                        name="firstname"
                        fullWidth
                        label="Firstname"
                        type="text"
                        error={!!errors.firstname && touched.firstname}
                        variant="outlined"
                        onChange={(event: any) => {
                          setFieldValue("firstname", event.target.value);
                          setFirstname(event.target.value);
                        }}
                        onBlur={handleBlur}
                        value={firstname}
                        helperText={touched.firstname && errors.firstname}
                      ></TextField>
                    </Box>
                    <Box mt={2}>
                      <TextField
                        id="lastname"
                        name="lastname"
                        fullWidth
                        label="Lastname"
                        type="text"
                        error={!!errors.lastname && touched.lastname}
                        variant="outlined"
                        onChange={(event: any) => {
                          setFieldValue("lastname", event.target.value);
                          setLastname(event.target.value);
                        }}
                        onBlur={handleBlur}
                        value={lastname}
                        helperText={touched.lastname && errors.lastname}
                      ></TextField>
                    </Box>
                    <Box mt={2}>
                      <TextField
                        margin="dense"
                        id="medicare"
                        name="medicare"
                        label="Medicare"
                        type="text"
                        fullWidth
                        variant="outlined"
                        inputProps={{
                          maxLength: 12,
                        }}
                        value={medicare}
                        onChange={(val) => {
                          const onlyNums = val.target.value.replace(/[^0-9]/g, "");
                          if (onlyNums.length === 11) {
                            const maskedNums = onlyNums.replace(/(\d{10})(\d{1})/, "$1/$2");
                            setFieldValue("medicare", maskedNums);
                            setMedicare(maskedNums);
                          } else {
                            setFieldValue("medicare", onlyNums);
                            setMedicare(onlyNums);
                          }
                        }}
                        onBlur={handleBlur}
                        error={touched.medicare && !!errors.medicare}
                        helperText={touched.medicare && errors.medicare}
                      />
                    </Box>
                    <Box mt={2}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          format="DD/MM/YYYY"
                          label="Patient DOB (DD/MM/YYYY)"
                          value={dob as Dayjs | null | undefined}
                          onChange={(value: Dayjs | null) => {
                            setFieldValue("dob", value);
                            setDob(value as Date | null);
                          }}
                          slotProps={{
                            textField: {
                              placeholder: "DD/MM/YYYY",
                              size: "small",
                              fullWidth: true, 
                              style: { width: "100%" }
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </Box>
                    <Box mt={2} display="flex" justifyContent="flex-end" alignItems="center">
                      {submitSuccess &&
                        (message === "unsubscribed" ? (
                          <Box mr={1} className={styles.successMessage}>
                            You have successfully unsubscribed.
                          </Box>
                        ) : (
                          <Box mr={1} className={styles.warnMessage}>
                            You have already unsubscribed or your info was not found.
                          </Box>
                        ))}
                      <Button type="submit" color="primary" variant="contained" disabled={loading}>
                        {loading ? (
                          <Loading
                            boxStyle={{ margin: "0" }}
                            iconStyle={{ color: "grey", height: "20px", width: "20px" }}
                          />
                        ) : (
                          "Submit"
                        )}
                      </Button>
                      <Button
                        sx={{ marginLeft: "5px" }}
                        type="button"
                        color="secondary"
                        variant="contained"
                        disabled={loading}
                        onClick={handleCancel}
                      >
                        Cancel
                      </Button>
                    </Box>
                    <Box>
                      <Typography color="primary" className={styles.footNote}>
                        All data is collected in strict compliance with Clinical Labs{" "}
                        <Link href="https://www.clinicallabs.com.au/about-us/privacy-policy/" target="_blank">
                          Privacy Policy
                        </Link>{" "}
                        By providing your details, you are agreeing to Clinical Labs{" "}
                        <Link href="https://ehealth.clinicallabs.com.au/terms_conditions.php" target="_blank">
                          Terms & Conditions
                        </Link>
                        .
                      </Typography>
                    </Box>
                  </form>
                );
              }}
            </Formik>
          </Box>
        </AuthLayout>
      </Grid>
    </Grid>
  );
};

const UnsubscribeDiabetesPage: React.FC<any> = () => {
  const [success, setSuccess] = useState(false);
  const [message, setMessage] = useState("");
  const [unsubscribeDiabeticPatient, { loading, error }] = useMutation<
    UnsubscribeDiabeticPatientMutation,
    UnsubscribeDiabeticPatientMutationVariables
  >(UnsubscribeDiabeticPatient);

  const onSubmit =
    (unsubscribeDiabeticPatient: useMutation.ResultTuple<UnsubscribeDiabeticPatientMutation, UnsubscribeDiabeticPatientMutationVariables>[0]) =>
    async (firstname: string, lastname: string, medicare: string, dob: string) => {
      setSuccess(false);
      setMessage("");

      try {
        const result = await unsubscribeDiabeticPatient({
          variables: {
            firstname,
            lastname,
            medicare,
            dob,
          },
        });

        if (!error) {
          setSuccess(true);

          if (get(result, "data.unsubscribeDiabeticPatient")) {
            setMessage(get(result, "data.unsubscribeDiabeticPatient", ""));
          }
        }
      } catch (e) {
        if (e.graphQLErrors) {
          setSuccess(false);
          setMessage("");
        }
      }
    };

  return (
    <Fragment>
      <MainNavigation hideTopMenu />
      <UnsubscribeDiabetesBox
        onSubmit={onSubmit(unsubscribeDiabeticPatient)}
        error={error}
        loading={loading}
        submitSuccess={success}
        message={message}
      />
    </Fragment>
  );
};

export default UnsubscribeDiabetesPage;
